import type { PageRoutine, RoutineFailure } from './routine'
import { RoutineError, routineFailureReason } from './routine'

export type UiOperation = { type: string } & Record<string, unknown>

export type UiOperationSource = { id: string; label?: string }

export type PendingUiOperation = {
  operation: UiOperation
  source?: UiOperationSource
}

export type UiOperationSuccess = { status: 'success'; value?: unknown }
export type UiOperationFailure = { status: 'error'; reason: RoutineFailure; message: string }
export type UiOperationResult = UiOperationSuccess | UiOperationFailure

export type OperationHandler = {
  ready: boolean
  handle: (operation: UiOperation) => Promise<unknown> | unknown
}

export type PageLoadState = 'loading' | 'ready' | 'failed'

export const PAGE_OPERATION_TYPES = ['ui_save', 'ui_discard'] as const

export type PageOperationType = (typeof PAGE_OPERATION_TYPES)[number]

export function isPageOperation(type: string): type is PageOperationType {
  return (PAGE_OPERATION_TYPES as readonly string[]).includes(type)
}

export async function runPageOperation(type: PageOperationType, routine: PageRoutine): Promise<unknown> {
  switch (type) {
    case 'ui_save':
      if (!routine.save) throw unsupported('This page cannot be saved')
      return routine.save()
    case 'ui_discard':
      if (!routine.discard) throw unsupported('This page has nothing to discard')
      return routine.discard()
  }
}

export function successResult(value: unknown): UiOperationSuccess {
  return value === undefined ? { status: 'success' } : { status: 'success', value }
}

export function errorResult(error: unknown): UiOperationFailure {
  return {
    status: 'error',
    reason: routineFailureReason(error),
    message: error instanceof Error ? error.message : String(error),
  }
}

export function unsupported(message: string): RoutineError {
  return new RoutineError('unsupported', message)
}
